import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiMessage } from "@/lib/team";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RoleSelect } from "@/components/admin/team/RoleSelect";

const EMPTY = { name: "", email: "", role: "support", notes: "" };
const label = "block text-xs font-semibold uppercase tracking-widest text-slate-500";
const field = "pf-input w-full px-3 py-2 mt-1 text-sm normal-case tracking-normal font-normal";

export function AddMemberDialog({ open, onClose, onDone, actorIsOwner }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const close = () => { if (busy) return; setForm(EMPTY); setError(""); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/team/invitations", { ...form, name: form.name.trim(), email: form.email.trim() });
      toast.success(`Invitation sent to ${form.email.trim()}`);
      setForm(EMPTY);
      onDone();
    } catch (err) {
      setError(apiMessage(err, "Couldn't send the invitation. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="team-add-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Add team member</DialogTitle>
            <DialogDescription>They get a one-time link (valid 72 hours) to choose their own password. Cyannect staff only.</DialogDescription>
          </DialogHeader>
          <label className={label}>Full name
            <input required maxLength={120} value={form.name} onChange={set("name")} className={field} data-testid="team-add-name" />
          </label>
          <label className={label}>Work email
            <input required type="email" value={form.email} onChange={set("email")} className={field} placeholder="name@cyannect.com" data-testid="team-add-email" />
          </label>
          <div>
            <div className={`${label} mb-2`}>Role</div>
            <RoleSelect value={form.role} onChange={set("role")} actorIsOwner={actorIsOwner} testid="team-add-role" />
          </div>
          <label className={label}>Internal notes (optional)
            <textarea rows={2} maxLength={1000} value={form.notes} onChange={set("notes")} className={field} data-testid="team-add-notes" />
          </label>
          {error && <p className="text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2" role="alert" data-testid="team-add-error">{error}</p>}
          <DialogFooter className="gap-2">
            <button type="button" onClick={close} disabled={busy} className="pf-btn-glass px-4 py-2 rounded-full text-sm" data-testid="team-add-cancel">Cancel</button>
            <button type="submit" disabled={busy || !form.name.trim() || !form.email.trim()} className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="team-add-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}{busy ? "Sending…" : "Send invitation"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
